import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useColor } from "@/Context/ColorContext";
import EmailPhoneTabs from "@/Components/Tabs/Tabs";
import InputTextField from "@/Components/Form/InputTextField";
import TypographyText from "@/Components/Form/TypographyText";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import { BackLink } from "@/Components/Buttons/Link";
import { LuMail, CiMobile2 } from "@/Components/Icons/Icons";
import { LiaCheckDoubleSolid, FaArrowLeft } from "@/Components/Icons/Icons";
import { navurl } from "@/Config/Routeconfig/NavigationUrl";
import { useEmailOtpMutation } from "@/redux/ForgotPassword/EmailOtp";
import { useResendOtpMutation } from "@/redux/ForgotPassword/ResendOtp";
import { Navigation } from "lucide-react";

function ForgotPassword() {
  const navigate = useNavigate();
  const { color } = useColor();

  const tabOptions = [
    { value: "email", label: "Email", icon: LuMail },
    { value: "phone", label: "Phone Number", icon: CiMobile2 },
  ];

  const [selected, setSelected] = useState("email");
  const [formData, setFormData] = useState({ otp: "" });
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  const [emailOtp, { isLoading }] = useEmailOtpMutation();
  const [resendOtp, { isLoading: resendOtpLoading }] = useResendOtpMutation();

  const userId = localStorage.getItem("userId") || "";
  const phone = localStorage.getItem("phone") || "";
  const maskedPhone = phone ? `+91 **** ${phone.slice(-4)}` : "+91 **** ****";

  const handleChange = (name, value) => {
    let numericValue = value.replace(/\D/g, "");
    if (numericValue.length > 6) numericValue = numericValue.slice(0, 6);
    setFormData((prev) => ({ ...prev, [name]: numericValue }));
  };

  const handleOtpRequestSubmit = async () => {
    const { otp } = formData;

    // Clear previous messages
    setErrorMessage("");
    setSuccessMessage("");

    // 🔥 Show error if OTP < 6
    if (!otp?.trim() || otp.length < 6) {
      setErrorMessage("OTP must be 6 digits."); // error shows under field
    }

    try {
      // Always hit API, even if OTP < 6
      const payload = { otp: otp.trim(), otpType: selected, userId };
      const userData = await emailOtp(payload).unwrap();

      if (userData?.data?.sessionId) {
        localStorage.setItem("sessionId", userData.data.sessionId);
        setSuccessMessage("OTP verified successfully!....");
        navigate(navurl.ResetPassword);  // ✅ Directly call navigate function
      } else {
        throw new Error("OTP verification failed.");
      }
    } catch (error) {
      console.error("OTP verification error:", error);

      // Show API error separately
      setErrorMessage(
        error?.data?.message ||
          error.message ||
          "Verification failed. Please try again."
      );
    }
  };

  const handleResendOtp = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    setResendLoading(true)

    if (!userId) {
      setErrorMessage("User ID is missing. Please go back and try again.");
      return;
    }

    try {
      const payload = { userId, otpType: selected };
      const response = await resendOtp(payload).unwrap();

      setSuccessMessage(response?.message || "OTP sent successfully.");
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (error) {
      console.error("Resend OTP error:", error);
      setErrorMessage(
        error?.data?.message || "Failed to resend OTP. Please try again."
      );
    }
    finally{
    setResendLoading(false);
    }
  };

  return (
    <Box
      sx={{
        py: 2,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      <Typography variant="heading" mb={2}>
        Forgot Password?
      </Typography>
      <Typography variant="bodyMedium" mb={2}>
        Two-factor authentication
      </Typography>

      <EmailPhoneTabs
        tabs={tabOptions}
        selected={selected}
        onChange={setSelected}
      />

      {/* Description */}
      {selected === "email" ? (
        <Typography mb={3} mt={2} variant="bodySmall" color="var(--gray-800)">
          OTP has been sent on your registered Email ID. Please contact
          Sys-Admin if OTP is not received.
        </Typography>
      ) : (
        <Box mb={3} mt={2} display="flex" flexDirection="column" gap={1}>
          <Typography variant="bodySmall" color="var(--gray-800)">
            We will send a verification code to
          </Typography>
          <Typography variant="bodySmall" sx={{ fontWeight: "bold" }}>
            {maskedPhone}
          </Typography>
        </Box>
      )}

      {/* OTP Input */}
      <InputTextField
        isOTPField={true} // passing the prop
        label="Enter the OTP"
        placeholder="Please Enter OTP"
        value={formData.otp}
        onChange={(e) => handleChange("otp", e.target.value)}
        maxLength={6}
        type="text"
        inputMode="numeric"
        required
        width="100%"
        sx={{ mb: 1 }}
        error={!!errorMessage} // 🔥 This line
        helperText={errorMessage || "Otp is required"} // 🔥 This line
        />

      {/* Resend OTP */}
      <TypographyText
        label="Click to Resend"
        actionText="Didn't receive the OTP?"
        loading={resendLoading} 
        onClick={handleResendOtp}
        sx={{ cursor: "pointer", width: "100%", mb: 2 }}
      ></TypographyText>

      {/* Submit */}
      <BlackButton
        label={isLoading ? "Verifying..." : "Submit"}
        onClick={handleOtpRequestSubmit}
        fullWidth
        disabled={isLoading}
        sx={{ mt: 2 }}
        width={"100%"}
      />

      {/* Back to Sign In */}
      <BackLink
        label="Back to Sign In"
        icon={FaArrowLeft}
        iconPosition="front"
        navigateTo={navurl.Login} // <-- use this
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
          gap: 1,
          width: "100%",
        }}
      />

      {successMessage && (
        <Typography color="success.main" mt={1} textAlign="center">
          {successMessage}
        </Typography>
      )}
    </Box>
  );
}

export default ForgotPassword;
